import { useEffect, useState } from "react";
import Navbar from "../../Navbar/NavbarComponent/Navbar";
import Loader from "../../../shared/components/Loader";
import { LABELS, toastError, toastSuccess } from "../../../shared/constants";
import authProvider from "../../../shared/config/authProvider";
import {
  WhatsAppStage,
  useWhatsappStagesListQuery,
  useWhatsappStagesToggleMutation,
} from "../../../Features/whatsapp/whatsapp-slice";

// WhatsApp notification stages: shows order events and lets an admin enable/disable
// the WhatsApp notification trigger for each stage using a toggle switch.
const WhatsAppStages = () => {
  const [stages, setStages] = useState<WhatsAppStage[]>([]);
  const [togglingStageId, setTogglingStageId] = useState<string | undefined>();

  const { data, isFetching, isSuccess, refetch } = useWhatsappStagesListQuery(
    undefined,
    { refetchOnMountOrArgChange: true },
  );

  const [toggleStage] = useWhatsappStagesToggleMutation();

  const canManage = authProvider.show("whatsapp", "manage");

  useEffect(() => {
    if (isSuccess && data) {
      setStages(data.list);
    }
  }, [data, isSuccess]);

  const handleToggle = async (stage: WhatsAppStage) => {
    if (!stage.hasTemplate && !stage.isEnabled) {
      toastError(
        "Cannot enable: No template configured for this stage. Please add a template in WhatsApp Templates first.",
      );
      return;
    }
    setTogglingStageId(stage.stageId);
    const response = await toggleStage({
      id: stage.stageId,
      data: { isEnabled: !stage.isEnabled },
    })
      .unwrap()
      .catch((err: any) => {
        toastError(
          err.data?.errors?.[0]?.msg || "Failed to update notification status",
        );
      });
    if (response && response.status) {
      toastSuccess(response.message);
      refetch();
    }
    setTogglingStageId(undefined);
  };

  return (
    <div className="full-height">
      <Navbar title={LABELS.WHATSAPP_STAGES_TEXT} />
      <main className="main-content">
        <div className="pt-3">
          <div className="container-fluid">
            <div className="card custom-card mb-4">
              <div className="card-header border-bottom">
                <h5 className="mb-0 text-black font-weight-600">
                  {LABELS.WHATSAPP_STAGES_TEXT}
                </h5>
              </div>
              <div className="card-body p-0">
                {isFetching && !stages.length ? (
                  <Loader />
                ) : (
                  <div className="table-responsive">
                    <table className="table table-header-shadow mb-0">
                      <thead>
                        <tr>
                          <th>{LABELS.STAGE_NAME_TEXT}</th>
                          <th>{LABELS.INTERAKT_TEMPLATE_NAME_TEXT}</th>
                          <th>{LABELS.STATUS_TEXT}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stages.length > 0 ? (
                          stages.map((stage) => {
                            const isLocked =
                              !canManage ||
                              togglingStageId === stage.stageId ||
                              !stage.hasTemplate;

                            return (
                              <tr key={stage.stageId}>
                                <td className="font-weight-600 text-dark">
                                  {stage.stageLabel}
                                </td>
                                <td>
                                  {stage.interaktTemplateName ? (
                                    <span className="badge bg-light text-dark border px-2 py-1">
                                      {stage.interaktTemplateName}
                                    </span>
                                  ) : (
                                    <span className="text-muted fst-italic">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="form-check form-switch mb-0">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id={`stage-switch-${stage.stageId}`}
                                        checked={stage.isEnabled}
                                        disabled={isLocked}
                                        onChange={() => handleToggle(stage)}
                                        style={{
                                          cursor: isLocked
                                            ? "not-allowed"
                                            : "pointer",
                                          backgroundColor: stage.isEnabled
                                            ? "#3E2215"
                                            : undefined,
                                          borderColor: stage.isEnabled
                                            ? "#3E2215"
                                            : undefined,
                                        }}
                                      />
                                    </div>
                                    <span
                                      className="ms-2"
                                      style={{
                                        cursor: isLocked
                                          ? "not-allowed"
                                          : "pointer",
                                        color: stage.isEnabled
                                          ? "#3E2215"
                                          : "#6c757d",
                                        fontWeight: 500,
                                      }}
                                      onClick={() =>
                                        !isLocked && handleToggle(stage)
                                      }
                                    >
                                      {stage.isEnabled
                                        ? LABELS.ENABLED_TEXT
                                        : LABELS.DISABLED_TEXT}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="text-center py-4 text-muted"
                            >
                              {LABELS.No_DATA_FOUND_TEXT}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WhatsAppStages;
